import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { User } from "../../types/user";
import HouseholdCard from "./HouseholdCard";

interface HouseholdMemberListState {
  peopleServedAndPrimary: User[];
  peopleServed: User[];
  primaryContact: User[];
  primaryAdults: User[];
  otherAdults: User[];
  children: User[];
  banned: User[];
  deleted: User[];
  currentUser: User | undefined;
  setCurrentUser: any;
  userToSave: boolean;
  setShowModal: any;
  createMode?: boolean;
}

// The grouped household cards: people served and main contacts first, then collapsible
// Adults, Children, Banned and Deleted sections.
const HouseholdMemberList = ({
  peopleServedAndPrimary,
  peopleServed,
  primaryContact,
  primaryAdults,
  otherAdults,
  children,
  banned,
  deleted,
  currentUser,
  setCurrentUser,
  userToSave,
  setShowModal,
  createMode = false,
}: HouseholdMemberListState) => {
  // Adults and Children are collapsed unless nobody ranks above them (people served, main contacts), so
  // the first person is never hidden in a closed section. That default follows the people as they change
  // (e.g. the last person served is removed); once someone opens or closes a section by hand, theirs wins.
  const nobodyAbove = peopleServedAndPrimary.length === 0 && peopleServed.length === 0 && primaryContact.length === 0;
  const hasAdults = primaryAdults.length > 0 || otherAdults.length > 0;
  const [adultsToggled, setAdultsToggled] = useState<boolean | undefined>(undefined);
  const [childrenToggled, setChildrenToggled] = useState<boolean | undefined>(undefined);
  const showAdults = adultsToggled ?? (nobodyAbove && hasAdults);
  const showChildren = childrenToggled ?? (nobodyAbove && !hasAdults && children.length > 0);
  const [showBanned, setShowBanned] = useState<boolean>(false);
  const [showDeleted, setShowDeleted] = useState<boolean>(false);

  const renderCard = (u: User, minWidth?: string) => (
    <div key={u.id + "-" + u.type} className="flex-shrink-0" style={minWidth ? { minWidth } : undefined}>
      <HouseholdCard
        user={u}
        currentUser={currentUser}
        setCurrentUser={setCurrentUser}
        userToSave={userToSave}
        setShowModal={setShowModal}
        createMode={createMode}
      />
    </div>
  );

  const renderSection = (title: string, list: User[], open: boolean, toggle: () => void) => (
    <>
      {list.length > 0 && (
        <h6 className="app-header-text cursor-pointer mb-0 mt-1" onClick={toggle}>
          {title} <FontAwesomeIcon icon={!open ? faAngleRight : faAngleDown} />
        </h6>
      )}
      {(open || window.innerWidth < 992) && list.map((u: User) => renderCard(u, "180px"))}
    </>
  );

  return (
    <>
      {peopleServedAndPrimary.map((u: User) => renderCard(u))}
      {peopleServed.map((u: User) => renderCard(u))}
      {primaryContact.map((u: User) => renderCard(u, "180px"))}
      {renderSection("Adults", [...primaryAdults, ...otherAdults], showAdults, () => setAdultsToggled(!showAdults))}
      {renderSection("Children", children, showChildren, () => setChildrenToggled(!showChildren))}
      {renderSection("Banned", banned, showBanned, () => setShowBanned(!showBanned))}
      {renderSection("Deleted", deleted, showDeleted, () => setShowDeleted(!showDeleted))}
    </>
  );
};

export default HouseholdMemberList;
